import * as Sentry from '@sentry/react';
import { VIZIT_ENV } from '@vizit/constants';
import * as React from 'react';

interface UsePendoAnalyticsProps {
  accountId?: string | null;
  accountName?: string | null;
  visitorId: string | null;
  visitorEmail?: string | null;
  dispatch: React.ActionDispatch<
    [
      action: {
        type: 'update' | 'clear';
        payload?: {
          accountId?: string | null;
          accountName?: string | null;
          visitorId?: string | null;
          visitorEmail?: string | null;
        };
      },
    ]
  >;
}

function usePendoAnalytics(props: UsePendoAnalyticsProps) {
  const { visitorId, dispatch } = props;
  // biome-ignore lint/correctness/useExhaustiveDependencies: Need to check if Pendo is loaded, otherwise we will get a type error.
  const handleOnIdentify = React.useCallback(
    (userId: string, organizationId?: string | null, organizationName?: string | null, userEmail?: string | null) => {
      if (!!window.pendo && !!window.pendo?.identify) {
        // In order to avoid collisions in Pendo, we need to prefix the user and account IDs with the environment.
        const envUserId = userId && VIZIT_ENV !== 'prod' ? `${VIZIT_ENV}-${userId}` : userId;
        const envAccountId = organizationId && VIZIT_ENV !== 'prod' ? `${VIZIT_ENV}-${organizationId}` : organizationId;

        try {
          if (!!window.pendo && !!window.pendo.identify) {
            // To debug Pendo, in browser console, enter pendo.enableDebugging(). This will show you all the Pendo configuration and events.
            window.pendo?.identify({
              visitor: {
                id: envUserId,
                email: userEmail,
              },
              ...(envAccountId && organizationName ? { account: { id: envAccountId, name: organizationName } } : {}),
            });

            dispatch({
              type: 'update',
              payload: {
                visitorId: userId,
                visitorEmail: userEmail,
                accountId: organizationId,
                accountName: organizationName,
              },
            });

            Sentry.addBreadcrumb({
              message: 'Pendo identification successful.',
              level: 'info',
              data: {
                user_id: userId,
                pendo_visitor_id: envUserId,
                organization_id: organizationId,
              },
            });
          } else {
            throw new Error('Pendo is not initialized and identify is not available');
          }
        } catch (error) {
          Sentry.withScope((scope) => {
            scope.setUser({
              id: userId,
              email: userEmail ?? undefined,
            });

            scope.setExtra('organization_name', organizationName);

            Sentry.addBreadcrumb({
              message: 'Pendo identification failed.',
              level: 'warning',
              data: { user_id: userId, organization_id: organizationId },
            });
          });
        }
      }
    },
    [!!window.pendo?.identify],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Need to check if Pendo is loaded, otherwise we will get a type error.
  const handleOnClearSession = React.useCallback(() => {
    try {
      if (!!window.pendo && !!window.pendo?.clearSession) {
        window.pendo?.clearSession();

        Sentry.addBreadcrumb({
          message: 'Pendo clear session successful.',
          level: 'info',
        });

        dispatch({ type: 'clear' });
      } else {
        throw new Error('Pendo is not initialized and clearSession is not available');
      }
    } catch (error) {
      Sentry.addBreadcrumb({
        message: 'Pendo clear session failed.',
        level: 'warning',
        data: { has_visitor_id: !!visitorId },
      });
    }
  }, [!!window.pendo?.clearSession]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Need to check if Pendo is loaded, otherwise we will get a type error.
  React.useEffect(() => {
    try {
      if (!!window.pendo && !!window.pendo?.getVisitorId && !visitorId) {
        const visitorId =
          VIZIT_ENV === 'prod'
            ? window.pendo.getVisitorId()
            : window.pendo.getVisitorId()?.replace(`${VIZIT_ENV}-`, '');

        const updatedPendoState = {
          visitorId,
        };

        Sentry.addBreadcrumb({
          message: 'Pendo get visitor ID successfully retrieved.',
          level: 'info',
          data: { visitor_id: visitorId, env: VIZIT_ENV },
        });

        dispatch({ type: 'update', payload: updatedPendoState });
      } else {
        throw new Error('Pendo is not initialized and getVisitorId is not available');
      }
    } catch (error) {
      Sentry.addBreadcrumb({
        message: 'Pendo get visitor ID failed.',
        level: 'warning',
        data: { has_visitor_id: !!visitorId },
      });
    }
  }, [!!window.pendo?.getVisitorId, visitorId]);

  return {
    handleOnIdentify,
    handleOnClearSession,
  };
}

export { usePendoAnalytics };
